<template>
  <v-card elevation="2" class="mb-6">
    <v-card-title class="d-flex justify-space-between align-center">
      <div>
        <v-icon left color="primary">mdi-cog</v-icon>
        <span class="text-h6">Top Services Report</span>
      </div>
      <div class="d-flex align-center">
        <v-select
          v-model="currentGroupBy"
          :items="groupByOptions"
          item-text="label"
          item-value="value"
          label="Group By"
          outlined
          dense
          class="mr-3"
          style="max-width: 200px;"
          @change="onGroupByChange"
          hide-details
        ></v-select>
        <v-btn
          color="primary"
          small
          outlined
          @click="downloadCSV"
          :loading="csvLoading"
          :disabled="csvLoading || !reportData || reportData.length === 0"
        >
          <v-icon left small>mdi-download</v-icon>
          Download CSV
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="reportData || []"
        :loading="loading"
        no-data-text="No data available"
        loading-text="Loading data..."
        :page.sync="pagination.page"
        :items-per-page.sync="pagination.itemsPerPage"
        :server-items-length="pagination.totalCount"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100]
        }"
        @update:items-per-page="onItemsPerPageChange"
        @update:page="onPageChange"
        @update:sort-by="onSortChange"
        @update:sort-desc="onSortChange"
        hide-default-footer
        class="elevation-0"
      >
        <template v-slot:item.service_name="{ item }">
          <v-btn
            v-if="currentGroupBy === 'service'"
            text
            color="primary"
            small
            @click="onServiceClick(item)"
          >
            <v-icon left small>mdi-cog</v-icon>
            {{ item.service_name }}
          </v-btn>
          <span v-else>{{ item.service_name || 'N/A' }}</span>
        </template>

        <template v-slot:item.vessel_name="{ item }">
          <v-btn
            v-if="currentGroupBy === 'vessel'"
            text
            color="primary"
            small
            @click="onVesselClick(item)"
          >
            <v-icon left small>mdi-ferry</v-icon>
            {{ item.vessel_name }}
          </v-btn>
          <span v-else>{{ item.vessel_name || 'N/A' }}</span>
        </template>

        <template v-slot:item.customer_name="{ item }">
          <v-btn
            v-if="currentGroupBy === 'customer'"
            text
            color="primary"
            small
            @click="onCustomerClick(item)"
          >
            <v-icon left small>mdi-account</v-icon>
            {{ item.customer_name }}
          </v-btn>
          <span v-else>{{ item.customer_name || 'N/A' }}</span>
        </template>

        <template v-slot:item.supplier_name="{ item }">
          <v-btn
            v-if="currentGroupBy === 'supplier'"
            text
            color="primary"
            small
            @click="onSupplierClick(item)"
          >
            <v-icon left small>mdi-truck-delivery</v-icon>
            {{ item.supplier_name }}
          </v-btn>
          <span v-else>{{ item.supplier_name || 'N/A' }}</span>
        </template>

        <template v-slot:item.suppliers_involved="{ item }">
          <div v-if="item.suppliers_involved && item.suppliers_involved.length > 0">
            <span
              v-for="(supplier, idx) in item.suppliers_involved"
              :key="supplier.id"
            >
              <v-btn
                text
                x-small
                color="primary"
                class="pa-0 ma-0"
                style="min-width: auto; text-transform: none;"
                @click="onSupplierNameClick(supplier.id)"
              >
                {{ supplier.name }}
              </v-btn>
              <span v-if="idx < item.suppliers_involved.length - 1">, </span>
            </span>
          </div>
          <span v-else class="grey--text">No suppliers</span>
        </template>

        <template v-slot:item.port_calls_count="{ item }">
          <v-btn
            text
            color="primary"
            small
            @click="onPortCallsClick(item)"
          >
            <v-chip color="info" small text-color="white">
              {{ item.port_calls_count || 0 }}
            </v-chip>
          </v-btn>
        </template>

        <template v-slot:item.vessels_count="{ item }">
          <v-chip color="success" small text-color="white">
            {{ item.vessels_count || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.customers_count="{ item }">
          <v-chip color="warning" small text-color="white">
            {{ item.customers_count || 0 }}
          </v-chip>
        </template>
      </v-data-table>

      <div v-if="reportData && reportData.length > 0" class="d-flex justify-space-between align-center mt-4">
        <div class="text-caption grey--text">
          Showing {{ ((pagination.page - 1) * pagination.itemsPerPage) + 1 }} to
          {{ Math.min(pagination.page * pagination.itemsPerPage, pagination.totalCount) }}
          of {{ pagination.totalCount }} entries
        </div>
        <div>
          <v-btn
            icon
            small
            :disabled="pagination.page === 1"
            @click="pagination.page = 1; loadReportData()"
          >
            <v-icon>mdi-page-first</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :disabled="pagination.page === 1"
            @click="pagination.page--; loadReportData()"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="mx-2">{{ pagination.page }} / {{ pagination.totalPages }}</span>
          <v-btn
            icon
            small
            :disabled="pagination.page >= pagination.totalPages"
            @click="pagination.page++; loadReportData()"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :disabled="pagination.page >= pagination.totalPages"
            @click="pagination.page = pagination.totalPages; loadReportData()"
          >
            <v-icon>mdi-page-last</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card-text>

    <v-dialog
      v-model="showPortCallDetail"
      max-width="90%"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6">Port Call Details</span>
          <v-btn icon @click="showPortCallDetail = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <port-call-detail-report
            :filters="portCallDetailFilters"
            :group-by="currentGroupBy"
            @close="showPortCallDetail = false"
          ></port-call-detail-report>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import PortCallDetailReport from './PortCallDetailReport.vue';

export default {
  name: 'TopServicesReport',
  components: {
    PortCallDetailReport
  },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      csvLoading: false,
      reportData: [],
      currentGroupBy: 'service',
      groupByOptions: [
        { label: 'Service', value: 'service' },
        { label: 'Vessel', value: 'vessel' },
        { label: 'Customer', value: 'customer' },
        { label: 'Supplier', value: 'supplier' }
      ],
      pagination: {
        page: 1,
        itemsPerPage: 25,
        totalCount: 0,
        totalPages: 1
      },
      sortBy: ['port_calls_count'],
      sortDesc: [true],
      showPortCallDetail: false,
      portCallDetailFilters: {}
    };
  },
  computed: {
    headers() {
      return this.getHeaders();
    }
  },
  watch: {
    filters: {
      handler() {
        this.pagination.page = 1;
        this.loadReportData();
      },
      deep: true
    }
  },
  methods: {
    getHeaders() {
      const baseHeaders = [];
      
      if (this.currentGroupBy === 'service') {
        baseHeaders.push({ text: 'Service Name', value: 'service_name', align: 'start', sortable: true });
      } else if (this.currentGroupBy === 'vessel') {
        baseHeaders.push({ text: 'Vessel Name', value: 'vessel_name', align: 'start', sortable: true });
      } else if (this.currentGroupBy === 'customer') {
        baseHeaders.push({ text: 'Customer Name', value: 'customer_name', align: 'start', sortable: true });
      } else if (this.currentGroupBy === 'supplier') {
        baseHeaders.push({ text: 'Supplier Name', value: 'supplier_name', align: 'start', sortable: true });
      }

      if (this.currentGroupBy === 'service') {
        baseHeaders.push({ text: 'Suppliers Involved', value: 'suppliers_involved', align: 'start', sortable: false });
        baseHeaders.push(
          { text: 'Port Calls', value: 'port_calls_count', align: 'center', sortable: true },
          { text: 'Vessels', value: 'vessels_count', align: 'center', sortable: true },
          { text: 'Customers', value: 'customers_count', align: 'center', sortable: true }
        );
      } else if (this.currentGroupBy === 'vessel') {
        baseHeaders.push({ text: 'Service Name', value: 'service_name', align: 'start', sortable: true });
        baseHeaders.push(
          { text: 'Port Calls', value: 'port_calls_count', align: 'center', sortable: true },
          { text: 'Customers', value: 'customers_count', align: 'center', sortable: true }
        );
      } else if (this.currentGroupBy === 'customer') {
        baseHeaders.push({ text: 'Service Name', value: 'service_name', align: 'start', sortable: true });
        baseHeaders.push(
          { text: 'Port Calls', value: 'port_calls_count', align: 'center', sortable: true },
          { text: 'Vessels', value: 'vessels_count', align: 'center', sortable: true }
        );
      } else if (this.currentGroupBy === 'supplier') {
        baseHeaders.push({ text: 'Service Name', value: 'service_name', align: 'start', sortable: true });
        baseHeaders.push(
          { text: 'Port Calls', value: 'port_calls_count', align: 'center', sortable: true },
          { text: 'Vessels', value: 'vessels_count', align: 'center', sortable: true }
        );
      }

      return baseHeaders;
    },
    
    async loadReportData() {
      this.loading = true;
      try {
        const params = {
          group_by: this.currentGroupBy,
          page: this.pagination.page,
          per_page: this.pagination.itemsPerPage,
          sort_by: this.sortBy && this.sortBy.length > 0 ? this.sortBy[0] : 'port_calls_count',
          sort_desc: this.sortDesc && this.sortDesc.length > 0 ? this.sortDesc[0] : true
        };
        
        if (this.filters) {
          if (this.filters.vessel_id) params.vessel_id = this.filters.vessel_id;
          if (this.filters.customer_id) params.customer_id = this.filters.customer_id;
          if (this.filters.service_id) params.service_id = this.filters.service_id;
          if (this.filters.supplier_id) params.supplier_id = this.filters.supplier_id;
          if (this.filters.start_date) params.start_date = this.filters.start_date;
          if (this.filters.end_date) params.end_date = this.filters.end_date;
        }
        
        const response = await this.$api.reports.getTopServicesDrilldown(params);
        const data = response.result || response;
        const reportDataArray = Array.isArray(data.report_data) ? data.report_data : [];
        
        const mappedData = reportDataArray.map(item => {
          return item;
        });
        
        this.$set(this, 'reportData', mappedData);
        this.pagination.totalCount = data.total_count || 0;
        this.pagination.totalPages = data.total_pages || 1;
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load report: ${error.message || 'Unknown error'}`,
        });
        this.reportData = [];
        this.pagination.totalCount = 0;
        this.pagination.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },
    
    onGroupByChange() {
      this.pagination.page = 1;
      this.loadReportData();
    },
    
    onServiceClick(item) {
      this.$emit('filter-change', item.service_id);
    },
    
    onVesselClick(item) {
      this.currentGroupBy = 'vessel';
      this.$emit('filter-change', null, { vessel_id: item.vessel_id });
      this.loadReportData();
    },
    
    onCustomerClick(item) {
      this.currentGroupBy = 'customer';
      this.$emit('filter-change', null, { customer_id: item.customer_id });
      this.loadReportData();
    },
    
    onSupplierClick(item) {
      this.currentGroupBy = 'supplier';
      this.$emit('filter-change', null, { supplier_id: item.supplier_id });
      this.loadReportData();
    },
    
    onPortCallsClick(item) {
      this.portCallDetailFilters = {
        ...this.filters,
        group_by: this.currentGroupBy,
        service_id: item.service_id || null,
        vessel_id: item.vessel_id || null,
        customer_id: item.customer_id || null,
        supplier_id: item.supplier_id || null
      };
      this.showPortCallDetail = true;
    },
    
    
    onPageChange(page) {
      this.pagination.page = page;
      this.loadReportData();
    },
    
    onItemsPerPageChange(itemsPerPage) {
      this.pagination.itemsPerPage = itemsPerPage;
      this.pagination.page = 1;
      this.loadReportData();
    },
    
    onSortChange() {
      this.pagination.page = 1;
      this.loadReportData();
    },
    
    onSupplierNameClick(supplierId) {
      this.$emit('filter-change', null, { supplier_id: supplierId });
    },
    
    async downloadCSV() {
      this.csvLoading = true;
      try {
        const params = {
          group_by: this.currentGroupBy,
          download_csv: 'true'
        };
        
        if (this.filters) {
          if (this.filters.vessel_id) params.vessel_id = this.filters.vessel_id;
          if (this.filters.customer_id) params.customer_id = this.filters.customer_id;
          if (this.filters.service_id) params.service_id = this.filters.service_id;
          if (this.filters.supplier_id) params.supplier_id = this.filters.supplier_id;
          if (this.filters.start_date) params.start_date = this.filters.start_date;
          if (this.filters.end_date) params.end_date = this.filters.end_date;
        }
        
        const queryString = Object.keys(params)
          .filter(key => params[key] != null && params[key] !== '')
          .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
          .join('&');
        
        const url = `/api/v1/reports/top_services_drilldown.csv?${queryString}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'text/csv',
          },
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to download CSV: ${errorText}`);
        }
        
        const blob = await response.blob();
        const url_blob = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url_blob;
        link.setAttribute('download', `top_services_report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url_blob);
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to download CSV: ${error.message}`,
        });
      } finally {
        this.csvLoading = false;
      }
    }
  },
  created() {
    this.loadReportData();
  }
};
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  border-radius: 8px;
}
</style>

